import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, FileText, Paperclip, Plus, Save, Trash2, Upload, X } from 'lucide-react'
import { config } from '@/core/config'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import {
  salesQuotationService,
  type SalesQuotationDetailLine,
  type SalesQuotationItemDetails,
  type SalesQuotationOption,
  type SalesQuotationSaveLine,
  type SalesQuotationSavePayload,
  type SalesQuotationTaxRate,
  type SalesQuotationWarehouseStockRow,
} from '@/services/salesQuotationService'
import { salesOrderService } from '@/services/salesOrderService'

import {
  MOCK_BATCHES_BY_ITEM,
  MOCK_CUSTOMERS,
  MOCK_PAYMENT_METHODS,
  MOCK_PAYMENT_TERMS,
  MOCK_WAREHOUSES,
  mockAvailableQuantity,
} from '../mockLookups'
import { getQuotation, upsertQuotation } from '../storage'
import type { InventoryQuotation, InventoryQuotationLine } from '../types'
import { lineDiscountAmount, lineGrossAmount, lineTaxableAmount, newQuotationLine } from '../types'

const today = () => new Date().toISOString().slice(0, 10)

const emptyLine = (): InventoryQuotationLine => ({
  ...newQuotationLine(),
  taxRateId: '',
})

/** Resolve a server-relative attachment path to an absolute URL the browser can open. */
function resolveAttachmentUrl(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

type AttachmentFieldProps = {
  inputId: string
  mode: 'new' | 'edit'
  file: File | null
  existingUrl: string
  existingName: string
  onFileChange: (file: File | null) => void
}

/** Polished single-line attachment control matching sales-order / dispatch. */
const AttachmentField: React.FC<AttachmentFieldProps> = ({
  inputId,
  mode,
  file,
  existingUrl,
  existingName,
  onFileChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const hasExisting = Boolean(existingUrl)
  const hasAnyAttachment = hasExisting || Boolean(file)
  const openPicker = () => fileInputRef.current?.click()
  const clearFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    onFileChange(null)
  }
  const fileName = file?.name || existingName || 'No file chosen'
  const showCard = mode === 'edit' && hasAnyAttachment

  return (
    <div className="space-y-1">
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        className="sr-only"
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        aria-label="File"
      />

      {showCard ? (
        <div className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20">
          <Paperclip className="h-4 w-4 shrink-0 text-primary" />
          {hasExisting && !file ? (
            <a
              href={existingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-sm text-foreground hover:text-primary hover:underline"
              title={fileName}
            >
              {fileName}
            </a>
          ) : (
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{fileName}</span>
          )}
          <div className="flex shrink-0 items-center gap-1">
            {hasExisting && !file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => window.open(existingUrl, '_blank', 'noopener,noreferrer')}
                title="View"
              >
                <Eye className="h-4 w-4" />
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 px-2" onClick={openPicker}>
              <Upload className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Replace</span>
            </Button>
            {file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                onClick={clearFile}
                title="Clear"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 text-left transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20"
        >
          <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm">
            {file ? (
              <span className="text-foreground">{file.name}</span>
            ) : (
              <span className="text-muted-foreground">Choose a file to upload</span>
            )}
          </span>
          {file ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                clearFile()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  clearFile()
                }
              }}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive"
              title="Clear"
            >
              <X className="h-4 w-4" />
            </span>
          ) : null}
        </button>
      )}

      {mode === 'edit' && file ? (
        <p className="truncate text-xs text-emerald-600 dark:text-emerald-400">
          New file selected — will replace on save
        </p>
      ) : null}
    </div>
  )
}

function resolveLineAvailableQuantity(
  l: InventoryQuotationLine,
  warehouseId: string,
  stockRows: SalesQuotationWarehouseStockRow[],
): number {
  const wh = Number(warehouseId || 0)
  const it = Number(l.itemId || 0)
  const batch = Number(l.batchId || 0)
  if (!it || !batch) return 0
  if (stockRows.length > 0) {
    if (wh > 0) {
      const hitWh = stockRows.find(
        (r) => Number(r.warehouseId) === wh && r.itemId === it && r.batchItemId === batch,
      )
      if (hitWh) return Math.max(0, hitWh.availableQuantity)
    }
    const hitAny = stockRows.find((r) => r.itemId === it && r.batchItemId === batch)
    if (hitAny) return Math.max(0, hitAny.availableQuantity)
  }
  if (wh > 0 && MOCK_BATCHES_BY_ITEM[l.itemId]?.some((x) => String(x.id) === String(batch))) {
    return mockAvailableQuantity(l.itemId, String(batch), String(wh))
  }
  return 0
}

function lineAvailableQuantity(
  l: InventoryQuotationLine,
  warehouseId: string,
  stockRows: SalesQuotationWarehouseStockRow[],
  itemDetailsByKey: Record<string, SalesQuotationItemDetails>,
): number {
  const wh = Number(warehouseId || 0)
  const it = Number(l.itemId || 0)
  const b = Number(l.batchId || 0)
  if (wh && it && b) {
    const k = `${wh}-${it}-${b}`
    const d = itemDetailsByKey[k]
    if (d !== undefined && Number.isFinite(d.availableQuantity)) return Math.max(0, d.availableQuantity)
  }
  return resolveLineAvailableQuantity(l, warehouseId, stockRows)
}

function newLineKey() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `ln-${Date.now()}-${Math.random()}`
}

function detailLinesToFormLines(rows: SalesQuotationDetailLine[], emptyLineFn: () => InventoryQuotationLine): InventoryQuotationLine[] {
  const active = rows.filter(
    (l) => !l.isDeleted && l.itemId > 0 && l.batchId > 0 && Number(l.quantity) > 0,
  )
  if (active.length === 0) return [emptyLineFn()]
  return active.map((l) => ({
    lineKey: newLineKey(),
    serverLineId: l.id > 0 ? l.id : undefined,
    itemId: String(l.itemId),
    batchId: String(l.batchId),
    availableQuantity: 0,
    quantity: l.quantity,
    unitPrice: l.unitPrice,
    discountPercent: l.discountPercent,
    taxRateId: l.taxRateId > 0 ? String(l.taxRateId) : '',
  }))
}

export const InventoryQuotationEdit: React.FC = () => {
  const { i18n } = useTranslation('common')
  const { id: routeId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = !routeId || routeId === 'new'
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)
  const userId = useAuthStore((s) => s.user?.userId ?? 0)

  const [quotationId, setQuotationId] = useState(() =>
    isNew ? `q_${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Date.now()}` : routeId!,
  )
  const [code, setCode] = useState('')
  const [orderDate, setOrderDate] = useState(today())
  const [estDeliveryDate, setEstDeliveryDate] = useState(today())
  const [reference, setReference] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [warehouseId, setWarehouseId] = useState('')
  const [paymentMethodId, setPaymentMethodId] = useState('')
  const [paymentTermId, setPaymentTermId] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [attachmentName, setAttachmentName] = useState('')
  /** Server-relative attachment path from the API (used for the View link). */
  const [serverAttachmentPath, setServerAttachmentPath] = useState('')
  const [status, setStatus] = useState<'draft' | 'cancelled'>('draft')
  const [createdAt, setCreatedAt] = useState(() => new Date().toISOString())
  const [lines, setLines] = useState<InventoryQuotationLine[]>(() => Array.from({ length: 3 }, () => emptyLine()))
  const [isSaving, setIsSaving] = useState(false)
  /** True while loading an existing quotation from GetSalesQuotationById + lines API. */
  const [editLoadingFromApi, setEditLoadingFromApi] = useState(
    () => Boolean(!isNew && routeId && /^\d+$/.test(routeId)),
  )

  const [customers, setCustomers] = useState<SalesQuotationOption[]>([])
  const [warehouses, setWarehouses] = useState<SalesQuotationOption[]>([])
  const [lookupsLoading, setLookupsLoading] = useState(false)
  const [lookupsReady, setLookupsReady] = useState(false)
  /** If saved id is missing from API list, show stored label until API includes it */
  const [fallbackCustomer, setFallbackCustomer] = useState<{ id: string; name: string } | null>(null)
  const [fallbackWarehouse, setFallbackWarehouse] = useState<{ id: string; name: string } | null>(null)

  const [paymentMethods, setPaymentMethods] = useState<SalesQuotationOption[]>([])
  const [paymentTerms, setPaymentTerms] = useState<SalesQuotationOption[]>([])
  const [paymentLookupsLoading, setPaymentLookupsLoading] = useState(false)
  const [paymentLookupsReady, setPaymentLookupsReady] = useState(false)

  const [warehouseStockRows, setWarehouseStockRows] = useState<SalesQuotationWarehouseStockRow[]>([])
  /** From GetItemsByWarehouse/{id} for the header warehouse */
  const [itemsForWarehouse, setItemsForWarehouse] = useState<SalesQuotationOption[]>([])
  const [itemsForWarehouseLoading, setItemsForWarehouseLoading] = useState(false)

  /** Key `${warehouseId}-${itemId}` → batches from GetBatchesByWarehouseAndItem (absent until loaded). */
  const [batchesByWarehouseItem, setBatchesByWarehouseItem] = useState<Record<string, SalesQuotationOption[]>>({})
  const batchesFetchedRef = useRef<Set<string>>(new Set())
  /** Same source as sales order: GetBatches(branchId, itemId). */
  const [batchesByItemId, setBatchesByItemId] = useState<Record<string, SalesQuotationOption[]>>({})
  const batchItemFetchedRef = useRef<Set<string>>(new Set())
  const lastQtyFetchKeyRef = useRef<Record<number, string>>({})
  /** Key `${warehouseId}-${itemId}-${batchId}` → GetItemDetails result */
  const [itemDetailsByKey, setItemDetailsByKey] = useState<Record<string, SalesQuotationItemDetails>>({})
  const itemDetailsLoadedRef = useRef<Set<string>>(new Set())
  const itemDetailsInFlightRef = useRef<Set<string>>(new Set())
  const lastDiscountFetchKeyRef = useRef<Record<number, string>>({})

  const [taxRates, setTaxRates] = useState<SalesQuotationTaxRate[]>([])
  const [taxRatesLoading, setTaxRatesLoading] = useState(false)

  /** Default tax rate for new/empty lines: prefer 15% (VAT), then API-flagged
   *  default, then any rate whose name contains "VAT", else the first one. */
  const defaultTaxRateId = useMemo(() => {
    const fifteen = taxRates.find((t) => Math.abs(Number(t.percent) - 15) < 0.0001 && t.id > 0)
    if (fifteen) return String(fifteen.id)
    const def = taxRates.find((t) => t.isDefault && t.id > 0)
    if (def) return String(def.id)
    const vat = taxRates.find(
      (t) => t.id > 0 && (/vat/i.test(String(t.nameEn || '')) || /VAT/i.test(String(t.nameAr || ''))),
    )
    if (vat) return String(vat.id)
    const first = taxRates.find((t) => t.id > 0)
    return first ? String(first.id) : ''
  }, [taxRates])

  const makeLine = useCallback((): InventoryQuotationLine => {
    return {
      ...newQuotationLine(),
      taxRateId: defaultTaxRateId,
    }
  }, [defaultTaxRateId])

  const taxLabel = useCallback(
    (t: SalesQuotationTaxRate) => {
      const ar = t.nameAr?.trim()
      const en = t.nameEn?.trim()
      if (i18n.language?.startsWith('ar') && ar) return ar
      return en || ar || `Tax ${t.id}`
    },
    [i18n.language],
  )

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        setTaxRatesLoading(true)
        const rates = await salesQuotationService.getAllTaxRates()
        if (cancelled) return
        const list = rates.filter((r) => r.id > 0)
        setTaxRates(list)
      } catch (error: unknown) {
        if (!cancelled) {
          const msg =
            error && typeof error === 'object' && 'response' in error
              ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
              : ''
          toast.error(msg || 'Failed to load tax rates.')
          setTaxRates([])
        }
      } finally {
        if (!cancelled) setTaxRatesLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!defaultTaxRateId) return
    setLines((prev) => {
      let changed = false
      const next = prev.map((l) => {
        if (l.taxRateId) return l
        changed = true
        return { ...l, taxRateId: defaultTaxRateId }
      })
      return changed ? next : prev
    })
  }, [defaultTaxRateId])

  useEffect(() => {
    if (!branchId) {
      setWarehouseStockRows([])
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const rows = await salesQuotationService.getAllWarehouseItems()
        if (cancelled) return
        setWarehouseStockRows(rows.filter((r) => r.itemId > 0))
      } catch (error: unknown) {
        if (!cancelled) {
          const msg =
            error && typeof error === 'object' && 'response' in error
              ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
              : ''
          toast.error(msg || 'Failed to load warehouse items.')
          setWarehouseStockRows([])
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [branchId])

  useEffect(() => {
    const wh = Number(warehouseId)
    if (!wh) {
      setItemsForWarehouse([])
      setItemsForWarehouseLoading(false)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        setItemsForWarehouseLoading(true)
        const list = await salesQuotationService.getItemsByWarehouse(wh)
        if (cancelled) return
        setItemsForWarehouse(list)
      } catch (error: unknown) {
        if (!cancelled) {
          const msg =
            error && typeof error === 'object' && 'response' in error
              ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
              : ''
          toast.error(msg || 'Failed to load items for warehouse.')
          setItemsForWarehouse([])
        }
      } finally {
        if (!cancelled) setItemsForWarehouseLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [warehouseId])

  useEffect(() => {
    setBatchesByWarehouseItem({})
    batchesFetchedRef.current.clear()
    setItemDetailsByKey({})
    itemDetailsLoadedRef.current.clear()
    itemDetailsInFlightRef.current.clear()
  }, [warehouseId])

  const lineItemSignature = useMemo(() => lines.map((l) => l.itemId).join('|'), [lines])

  useEffect(() => {
    const wh = Number(warehouseId)
    if (!wh) return
    const ids = [...new Set(lines.map((l) => Number(l.itemId || 0)).filter((x) => x > 0))]
    let cancelled = false
    for (const it of ids) {
      const key = `${wh}-${it}`
      if (batchesFetchedRef.current.has(key)) continue
      batchesFetchedRef.current.add(key)
      void (async () => {
        try {
          const list = await salesQuotationService.getBatchesByWarehouseAndItem(wh, it)
          if (cancelled) return
          setBatchesByWarehouseItem((prev) => ({ ...prev, [key]: list }))
        } catch {
          if (!cancelled) {
            toast.error('Failed to load batches for an item.')
            setBatchesByWarehouseItem((prev) => ({ ...prev, [key]: [] }))
          }
          batchesFetchedRef.current.delete(key)
        }
      })()
    }
    return () => {
      cancelled = true
    }
  }, [warehouseId, lineItemSignature])

  const lineBatchSignature = useMemo(
    () => lines.map((l) => `${l.itemId}:${l.batchId}`).join('|'),
    [lines],
  )

  const itemDetailsFetchTargets = useMemo(() => {
    const wh = Number(warehouseId)
    if (!wh) return [] as { key: string; it: number; b: number }[]
    const parts = lineBatchSignature.split('|').filter(Boolean)
    const seen = new Set<string>()
    const out: { key: string; it: number; b: number }[] = []
    for (const p of parts) {
      const [itemStr, batchStr] = p.split(':')
      const it = Number(itemStr)
      const b = Number(batchStr)
      if (!it || !b) continue
      const key = `${wh}-${it}-${b}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push({ key, it, b })
    }
    return out
  }, [warehouseId, lineBatchSignature])

  useEffect(() => {
    const whNum = Number(warehouseId)
    if (!whNum) return
    let cancelled = false
    for (const { key, it, b } of itemDetailsFetchTargets) {
      if (itemDetailsLoadedRef.current.has(key) || itemDetailsInFlightRef.current.has(key)) continue
      itemDetailsInFlightRef.current.add(key)
      void (async () => {
        try {
          const d = await salesQuotationService.getItemDetails(whNum, it, b)
          itemDetailsInFlightRef.current.delete(key)
          if (cancelled) return
          itemDetailsLoadedRef.current.add(key)
          setItemDetailsByKey((prev) => ({ ...prev, [key]: d }))
          setLines((prev) =>
            prev.map((row) => {
              if (Number(row.itemId) !== it || Number(row.batchId) !== b) return row
              const avail = Math.max(0, d.availableQuantity)
              const qty = avail > 0 ? Math.min(row.quantity, avail) : row.quantity
              return {
                ...row,
                availableQuantity: avail,
                unitPrice: d.unitPrice > 0 ? d.unitPrice : row.unitPrice,
                quantity: qty,
              }
            }),
          )
        } catch {
          itemDetailsInFlightRef.current.delete(key)
          if (!cancelled) {
            toast.error('Failed to load item details for a line.')
          }
        }
      })()
    }
    return () => {
      cancelled = true
    }
  }, [warehouseId, itemDetailsFetchTargets])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        setPaymentLookupsReady(false)
        setPaymentLookupsLoading(true)
        const [m, t] = await Promise.all([
          salesQuotationService.getPaymentMethods(),
          salesQuotationService.getPaymentTerms(),
        ])
        if (cancelled) return
        const mList = m.filter((x) => x.id > 0 && x.name.trim())
        const tList = t.filter((x) => x.id > 0 && x.name.trim())
        setPaymentMethods(mList.length ? mList : MOCK_PAYMENT_METHODS)
        setPaymentTerms(tList.length ? tList : MOCK_PAYMENT_TERMS)
      } catch (error: unknown) {
        if (!cancelled) {
          const msg =
            error && typeof error === 'object' && 'response' in error
              ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
              : ''
          toast.error(msg || 'Failed to load payment methods / terms.')
          setPaymentMethods(MOCK_PAYMENT_METHODS)
          setPaymentTerms(MOCK_PAYMENT_TERMS)
        }
      } finally {
        if (!cancelled) {
          setPaymentLookupsLoading(false)
          setPaymentLookupsReady(true)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!branchId) {
      setLookupsReady(true)
      setCustomers(MOCK_CUSTOMERS)
      setWarehouses(MOCK_WAREHOUSES)
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        setLookupsReady(false)
        setLookupsLoading(true)
        const [c, w] = await Promise.all([
          salesQuotationService.getCustomers(branchId),
          salesQuotationService.getWarehouses(branchId),
        ])
        if (cancelled) return
        const cList = c.filter((x) => x.id > 0 && x.name.trim())
        const wList = w.filter((x) => x.id > 0 && x.name.trim())
        setCustomers(cList.length ? cList : MOCK_CUSTOMERS)
        setWarehouses(wList.length ? wList : MOCK_WAREHOUSES)
        if (isNew) {
          const dist = wList.find((wh) => wh.isWarehouseDistribution)
          if (dist) setWarehouseId(String(dist.id))
          else toast.error('Distribution warehouse is not configured for this branch.')
        }
      } catch (error: unknown) {
        if (!cancelled) {
          const msg =
            error && typeof error === 'object' && 'response' in error
              ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
              : ''
          toast.error(msg || 'Failed to load customers / warehouses.')
          setCustomers(MOCK_CUSTOMERS)
          setWarehouses(MOCK_WAREHOUSES)
        }
      } finally {
        if (!cancelled) {
          setLookupsLoading(false)
          setLookupsReady(true)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [branchId, isNew])

  useEffect(() => {
    if (fallbackCustomer && customers.some((c) => String(c.id) === fallbackCustomer.id)) {
      setFallbackCustomer(null)
    }
  }, [customers, fallbackCustomer])

  useEffect(() => {
    if (fallbackWarehouse && warehouses.some((w) => String(w.id) === fallbackWarehouse.id)) {
      setFallbackWarehouse(null)
    }
  }, [warehouses, fallbackWarehouse])

  useEffect(() => {
    if (isNew) {
      setEditLoadingFromApi(false)
      setQuotationId(`q_${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Date.now()}`)
      setCode('')
      setOrderDate(today())
      setEstDeliveryDate(today())
      setReference('')
      setCustomerId('')
      setWarehouseId('')
      setPaymentMethodId('')
      setPaymentTermId('')
      setAttachmentFile(null)
      setAttachmentName('')
      setStatus('draft')
      setCreatedAt(new Date().toISOString())
      setLines(Array.from({ length: 3 }, () => makeLine()))
      setFallbackCustomer(null)
      setFallbackWarehouse(null)
      return
    }

    if (!routeId) {
      setEditLoadingFromApi(false)
      return
    }

    const loadFromApi = /^\d+$/.test(routeId)

    if (!loadFromApi) {
      setEditLoadingFromApi(false)
      const doc = getQuotation(routeId)
      if (!doc) {
        toast.error('Quotation not found.')
        navigate('/inventory/quotations')
        return
      }

      setQuotationId(doc.id)
      setCode(doc.code)
      setOrderDate(doc.orderDate || today())
      setEstDeliveryDate(doc.estDeliveryDate || today())
      setReference(doc.reference)
      setCustomerId(doc.customerId)
      setWarehouseId(doc.warehouseId)
      setPaymentMethodId(doc.paymentMethodId)
      setPaymentTermId(doc.paymentTermId)
      setAttachmentName(doc.attachmentName || '')
      setAttachmentFile(null)
      setStatus(doc.status)
      setCreatedAt(doc.createdAt)
      setFallbackCustomer(doc.customerId ? { id: doc.customerId, name: doc.customerName } : null)
      setFallbackWarehouse(doc.warehouseId ? { id: doc.warehouseId, name: doc.warehouseName } : null)
      setLines(
        doc.lines?.length
          ? doc.lines.map((l) => ({
              ...l,
              lineKey: l.lineKey || newLineKey(),
            }))
          : [makeLine()],
      )
      return
    }

    const id = Number(routeId)
    setEditLoadingFromApi(true)
    let cancelled = false
    void (async () => {
      try {
        const [master, lineRows] = await Promise.all([
          salesQuotationService.getSalesQuotationById(id),
          salesQuotationService.getSalesQuotationLinesById(id),
        ])
        if (cancelled) return
        if (!master.id) {
          toast.error('Quotation not found.')
          navigate('/inventory/quotations')
          return
        }

        setQuotationId(String(master.id))
        setCode(master.quotCode.trim() || master.code.trim() || '')
        setOrderDate(master.orderDate || today())
        setEstDeliveryDate(master.estDeliveryDate || today())
        setReference(master.reference)
        setCustomerId(master.customerId > 0 ? String(master.customerId) : '')
        setWarehouseId(master.warehouseId > 0 ? String(master.warehouseId) : '')
        setPaymentMethodId(master.paymentMethod > 0 ? String(master.paymentMethod) : '')
        setPaymentTermId(master.paymentTerm > 0 ? String(master.paymentTerm) : '')
        setAttachmentName(master.attachmentPath || '')
        setServerAttachmentPath(master.attachmentPath || '')
        setAttachmentFile(null)
        setStatus(master.orderStatus === 5 ? 'cancelled' : 'draft')
        setCreatedAt(master.createdDate || new Date().toISOString())
        setFallbackCustomer(
          master.customerId > 0 ? { id: String(master.customerId), name: master.customerName } : null,
        )
        setFallbackWarehouse(
          master.warehouseId > 0 ? { id: String(master.warehouseId), name: master.warehouseName } : null,
        )
        setLines(
          detailLinesToFormLines(lineRows, () => ({
            ...newQuotationLine(),
            taxRateId: '',
          })),
        )
      } catch (error: unknown) {
        if (!cancelled) {
          const msg =
            error && typeof error === 'object' && 'response' in error
              ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
              : ''
          toast.error(msg || 'Failed to load quotation.')
          navigate('/inventory/quotations')
        }
      } finally {
        if (!cancelled) setEditLoadingFromApi(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isNew, routeId, navigate])

  useEffect(() => {
    if (!isNew || !lookupsReady || warehouses.length === 0) return
    setWarehouseId((prev) => (prev ? prev : String(warehouses[0].id)))
  }, [isNew, lookupsReady, warehouses])

  useEffect(() => {
    if (!isNew || !paymentLookupsReady) return
    setPaymentMethodId((prev) => prev || (paymentMethods[0] ? String(paymentMethods[0].id) : ''))
    setPaymentTermId((prev) => prev || (paymentTerms[0] ? String(paymentTerms[0].id) : ''))
  }, [isNew, paymentLookupsReady, paymentMethods, paymentTerms])

  const lineTaxPercent = (l: InventoryQuotationLine) =>
    taxRates.find((t) => String(t.id) === l.taxRateId)?.percent ?? 0

  const lineTaxAmount = (l: InventoryQuotationLine) => lineTaxableAmount(l) * (lineTaxPercent(l) / 100)
  const lineTotal = (l: InventoryQuotationLine) => lineTaxableAmount(l) + lineTaxAmount(l)

  const taxSelectOptions = useMemo(() => {
    const rows = taxRates.map((t) => ({
      value: String(t.id),
      label: `${taxLabel(t)} (${t.percent}%)`,
    }))
    const seen = new Set(rows.map((r) => r.value))
    for (const l of lines) {
      const tid = l.taxRateId?.trim()
      if (!tid || seen.has(tid)) continue
      seen.add(tid)
      rows.push({ value: tid, label: `Tax #${tid}` })
    }
    return rows
  }, [taxRates, lines, taxLabel])

  /** Same rules as purchase order lines: gross subtotal, line discount (currency), tax on taxable, net = taxable + tax. */
  const totals = useMemo(() => {
    const pct = (tid: string) => taxRates.find((t) => String(t.id) === tid)?.percent ?? 0
    const subTotal = lines.reduce((sum, l) => sum + lineGrossAmount(l), 0)
    const discount = lines.reduce((sum, l) => sum + lineDiscountAmount(l), 0)
    const taxable = lines.reduce((sum, l) => sum + lineTaxableAmount(l), 0)
    const totalTax = lines.reduce((sum, l) => sum + lineTaxableAmount(l) * (pct(l.taxRateId) / 100), 0)
    const net = Math.max(0, taxable + totalTax)
    return { subTotal, discount, taxable, totalTax, net }
  }, [lines, taxRates])

  /** GetItemsByWarehouse for selected warehouse; merge saved line itemIds so labels stay visible. */
  const itemOptionsForWarehouse = useMemo(() => {
    const byId = new Map<number, SalesQuotationOption>()
    for (const o of itemsForWarehouse) {
      if (o.id > 0) byId.set(o.id, o)
    }
    for (const ln of lines) {
      const iid = Number(ln.itemId || 0)
      if (iid <= 0) continue
      if (!byId.has(iid)) {
        byId.set(iid, { id: iid, name: String(iid) })
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.id - b.id)
  }, [itemsForWarehouse, lines])

  const loadBatchesForItem = async (itemId: string) => {
    const bId = Number(branchId || 0)
    const itId = Number(itemId || 0)
    const whId = Number(warehouseId || 0)
    if (!bId || !itId) return
    const cacheKey = `${bId}-${whId}-${itId}`
    if (batchItemFetchedRef.current.has(cacheKey)) return
    batchItemFetchedRef.current.add(cacheKey)
    try {
      const batches = await salesOrderService.getBatches(bId, itId, whId)
      setBatchesByItemId((prev) => ({ ...prev, [itemId]: batches }))
    } catch {
      batchItemFetchedRef.current.delete(cacheKey)
      setBatchesByItemId((prev) => ({ ...prev, [itemId]: [] }))
    }
  }

  const batchesForLineItem = useCallback(
    (itemId: string) => {
      if (!itemId) return []
      const fromOrderApi = batchesByItemId[itemId]
      if (fromOrderApi && fromOrderApi.length > 0) return fromOrderApi
      const wh = Number(warehouseId || 0)
      const it = Number(itemId || 0)
      if (!it) return []
      const apiKey = `${wh}-${it}`
      if (wh > 0 && Object.prototype.hasOwnProperty.call(batchesByWarehouseItem, apiKey)) {
        return batchesByWarehouseItem[apiKey] ?? []
      }
      if (fromOrderApi) return fromOrderApi
      if (warehouseStockRows.length > 0) {
        const collect = (filterByWh: boolean) => {
          const seen = new Set<number>()
          const out: SalesQuotationOption[] = []
          for (const r of warehouseStockRows) {
            if (r.itemId !== it || r.batchItemId <= 0) continue
            if (filterByWh && wh > 0 && Number(r.warehouseId) !== wh) continue
            if (seen.has(r.batchItemId)) continue
            seen.add(r.batchItemId)
            const whLabel = filterByWh ? '' : ` (WH ${r.warehouseId})`
            out.push({ id: r.batchItemId, name: `Batch ${r.batchItemId}${whLabel}` })
          }
          return out
        }
        const scoped = collect(true)
        if (scoped.length > 0) return scoped
        const anyWh = collect(false)
        if (anyWh.length > 0) return anyWh
      }
      return wh > 0 ? MOCK_BATCHES_BY_ITEM[itemId] ?? [] : []
    },
    [warehouseId, warehouseStockRows, batchesByWarehouseItem, batchesByItemId],
  )

  const rowAvailable = useCallback(
    (l: InventoryQuotationLine) =>
      lineAvailableQuantity(l, warehouseId, warehouseStockRows, itemDetailsByKey),
    [warehouseId, warehouseStockRows, itemDetailsByKey],
  )

  const refreshLineDiscountPercent = async (
    index: number,
    next?: { itemId?: string; batchId?: string },
  ) => {
    const bId = Number(branchId || 0)
    const itId = Number((next?.itemId ?? lines[index]?.itemId) || 0)
    const batchId = Number((next?.batchId ?? lines[index]?.batchId) || 0)
    const oDate = String(orderDate || '').trim()
    if (!bId || !itId || !oDate) return
    try {
      const pct = await salesOrderService.getDiscountPackagePercent({
        itemId: itId,
        branchId: bId,
        batchId,
        orderDate: oDate,
      })
      updateLine(index, { discountPercent: pct ?? 0 })
    } catch {
      // If discount API fails, keep the current scheme value.
    }
  }

  const updateLine = (index: number, patch: Partial<InventoryQuotationLine>) => {
    setLines((prev) => {
      const next = [...prev]
      const row = next[index]
      if (!row) return prev
      const merged: InventoryQuotationLine = { ...row, ...patch }
      if (patch.itemId !== undefined && patch.itemId !== row.itemId) {
        merged.batchId = ''
        merged.availableQuantity = 0
        merged.quantity = 0
        merged.discountPercent = 0
        delete lastDiscountFetchKeyRef.current[index]
      }
      if (patch.batchId !== undefined && patch.batchId !== row.batchId) {
        merged.availableQuantity = 0
        merged.quantity = 0
        merged.discountPercent = 0
        delete lastDiscountFetchKeyRef.current[index]
      }
      const avail = Number(merged.availableQuantity || 0)
      merged.quantity = Math.min(Number(merged.quantity || 0), avail || Number(merged.quantity || 0))
      next[index] = merged
      return next
    })
  }

  const refreshAvailableQuantity = async (index: number, next?: { itemId?: string; batchId?: string }) => {
    const whId = Number(warehouseId || 0)
    if (!whId) return
    const row = lines[index]
    if (!row) return
    const itemId = Number((next?.itemId ?? row.itemId) || 0)
    const batchId = Number((next?.batchId ?? row.batchId) || 0)
    if (!itemId || !batchId) return
    try {
      const qty = await salesOrderService.getWarehouseItemQuantity(whId, batchId, itemId)
      updateLine(index, { availableQuantity: qty })
    } catch {
      updateLine(index, { availableQuantity: 0 })
    }
  }

  useEffect(() => {
    batchItemFetchedRef.current.clear()
    setBatchesByItemId({})
  }, [branchId])

  useEffect(() => {
    const ids = [...new Set(lines.map((l) => l.itemId).filter(Boolean))]
    ids.forEach((id) => {
      void loadBatchesForItem(id)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, lineItemSignature])

  useEffect(() => {
    const whId = Number(warehouseId || 0)
    if (!whId) return
    lines.forEach((row, idx) => {
      const itemId = Number(row.itemId || 0)
      const batchId = Number(row.batchId || 0)
      if (!itemId || !batchId) return
      const key = `${whId}-${itemId}-${batchId}`
      if (lastQtyFetchKeyRef.current[idx] === key) return
      lastQtyFetchKeyRef.current[idx] = key
      void refreshAvailableQuantity(idx, { itemId: row.itemId, batchId: row.batchId })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, lines])

  useEffect(() => {
    const bId = Number(branchId || 0)
    const oDate = String(orderDate || '').trim()
    if (!bId || !oDate) return

    lines.forEach((row, idx) => {
      const itemId = Number(row.itemId || 0)
      if (!itemId) return
      const batchId = Number(row.batchId || 0)
      const key = `${bId}-${oDate}-${itemId}-${batchId}`
      if (lastDiscountFetchKeyRef.current[idx] === key) return
      if (!isNew && lastDiscountFetchKeyRef.current[idx] === undefined) {
        lastDiscountFetchKeyRef.current[idx] = key
        return
      }
      lastDiscountFetchKeyRef.current[idx] = key
      void refreshLineDiscountPercent(idx, { itemId: row.itemId, batchId: row.batchId })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, orderDate, lines, isNew])

  const addLines = (n: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: n }, () => makeLine())])
  }

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  const customerRows = useMemo(() => {
    const rows = [...customers]
    if (fallbackCustomer && !rows.some((c) => String(c.id) === fallbackCustomer.id)) {
      const nid = Number(fallbackCustomer.id)
      if (Number.isFinite(nid) && nid > 0) {
        rows.unshift({ id: nid, name: fallbackCustomer.name })
      }
    }
    return rows
  }, [customers, fallbackCustomer])

  const warehouseRows = useMemo(() => {
    const rows = [...warehouses]
    if (fallbackWarehouse && !rows.some((w) => String(w.id) === fallbackWarehouse.id)) {
      const nid = Number(fallbackWarehouse.id)
      if (Number.isFinite(nid) && nid > 0) {
        rows.unshift({ id: nid, name: fallbackWarehouse.name })
      }
    }
    return rows
  }, [warehouses, fallbackWarehouse])

  const customerName = customerRows.find((c) => String(c.id) === customerId)?.name ?? ''
  const warehouseName = warehouseRows.find((w) => String(w.id) === warehouseId)?.name ?? ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const orderStatusForPayload = status === 'cancelled' ? 5 : 0
    if (status === 'cancelled') {
      toast.error('Cancelled quotations cannot be saved.')
      return
    }
    if (!reference.trim()) {
      toast.error('Reference is required.')
      return
    }
    if (!customerId) {
      toast.error('Customer is required.')
      return
    }
    if (!warehouseId) {
      toast.error('Warehouse is required.')
      return
    }
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!paymentMethodId) {
      toast.error('Payment method is required.')
      return
    }
    if (!paymentTermId) {
      toast.error('Payment term is required.')
      return
    }

    const activeFormLines = lines.filter(
      (l) => Number(l.itemId) > 0 && Number(l.batchId) > 0 && Number(l.quantity) > 0 && l.taxRateId,
    )
    if (activeFormLines.length === 0) {
      toast.error('Add at least one line with item, batch, quantity, and tax.')
      return
    }
    if (activeFormLines.some((l) => !Number(l.taxRateId))) {
      toast.error('Select a tax rate for every line item.')
      return
    }
    const overQty = activeFormLines.find((l) => {
      const avail = Number(l.availableQuantity || rowAvailable(l) || 0)
      const qty = Number(l.quantity || 0)
      return avail > 0 && qty > avail
    })
    if (overQty) {
      toast.error('Quantity cannot exceed available batch quantity.')
      return
    }
    if (activeFormLines.some((l) => Number(l.batchId || 0) <= 0)) {
      toast.error('Select a batch for every item.')
      return
    }

    const now = new Date().toISOString()
    const nextCode = code.trim() || `QUO-${Date.now().toString(36).toUpperCase()}`
    const numericServerId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : 0
    const quotationOrderId = numericServerId
    const isApiUpdate = numericServerId > 0

    const itemList: SalesQuotationSaveLine[] = activeFormLines.map((l) => {
      const qty = Number(l.quantity || 0)
      const unitPrice = Number(l.unitPrice || 0)
      const discountPercent = Number(l.discountPercent || 0)
      const taxableAmount = lineTaxableAmount(l)
      const taxAmount = lineTaxAmount(l)
      const lineAmount = lineTotal(l)
      const gross = lineGrossAmount(l)
      const discountAmount = Math.max(0, gross - taxableAmount)
      const taxRateIdNum = Number(l.taxRateId || 0)
      const taxPercent = lineTaxPercent(l)
      const lineServerId = isApiUpdate ? Number(l.serverLineId ?? 0) : 0
      return {
        id: lineServerId,
        orderId: quotationOrderId,
        itemId: Number(l.itemId || 0),
        batchId: Number(l.batchId || 0),
        quantity: qty,
        unitPrice,
        discountPercent,
        discountAmount,
        taxPercent,
        taxAmount,
        taxableAmount,
        lineAmount,
        unitCostPrice: 0,
        tax: taxRateIdNum,
        isDeleted: false,
      }
    })

    const salesQuotationLinesJson = JSON.stringify(itemList)
    const headerTaxable = itemList.reduce((s, r) => s + r.taxableAmount, 0)
    const headerTax = itemList.reduce((s, r) => s + r.taxAmount, 0)
    const headerDiscount = itemList.reduce((s, r) => s + r.discountAmount, 0)
    const headerTotal = itemList.reduce((s, r) => s + r.lineAmount, 0)

    const payload: SalesQuotationSavePayload = {
      id: numericServerId,
      quotCode: code.trim() || nextCode,
      code: nextCode,
      customerName,
      warehouseName,
      reference: reference.trim(),
      orderDate,
      estDeliveryDate,
      customerId: Number(customerId || 0),
      paymentMethod: Number(paymentMethodId || 0),
      paymentTerm: Number(paymentTermId || 0),
      taxableAmount: headerTaxable,
      taxAmount: headerTax,
      discount: headerDiscount,
      total: headerTotal,
      salesPersonId: 0,
      warehouseId: Number(warehouseId || 0),
      orderStatus: orderStatusForPayload,
      attachmentPath: attachmentName || '',
      createdDate: createdAt,
      createdBy: userId,
      modifiedDate: now,
      modifiedBy: userId,
      branchId: Number(branchId),
      attachmentFile,
      itemList,
    }

    const { attachmentFile: _af, itemList: _il, ...masterForLog } = payload
    const saveAction = isApiUpdate ? 'Update' : 'Save'
    console.log(`[SalesQuotation] ${saveAction} — structure (matches multipart + lines)`, {
      ...masterForLog,
      SalesQuotationLinesJson: salesQuotationLinesJson,
      ItemList: itemList,
      AttachmentFile: attachmentFile ? attachmentFile.name : null,
    })

    try {
      setIsSaving(true)
      const apiResponse = isApiUpdate
        ? await salesQuotationService.update(payload)
        : await salesQuotationService.save(payload)

      const doc: InventoryQuotation = {
        id: quotationId,
        branchId: Number(branchId),
        code: nextCode,
        reference: reference.trim(),
        customerId,
        customerName,
        warehouseId,
        warehouseName,
        paymentMethodId,
        paymentTermId,
        orderDate,
        estDeliveryDate,
        attachmentName: attachmentFile?.name || attachmentName || undefined,
        orderDiscount: 0,
        lines: lines.map((l) => ({
          ...l,
          availableQuantity: lineAvailableQuantity(l, warehouseId, warehouseStockRows, itemDetailsByKey),
        })),
        status,
        createdAt,
        updatedAt: now,
      }
      upsertQuotation(doc)

      console.log(`[SalesQuotation] ${saveAction} — API response:`, apiResponse)

      await Swal.fire({
        icon: 'success',
        title: isApiUpdate ? 'Quotation updated' : 'Quotation saved',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3b82f6',
      })
      navigate('/inventory/quotations')
    } catch (error: unknown) {
      const ax = error as {
        response?: { status?: number; statusText?: string; data?: unknown; headers?: unknown }
        message?: string
      }
      const failLabel = `[SalesQuotation] ${saveAction} failed`
      if (ax.response) {
        const { status, statusText, data, headers } = ax.response
        console.error(`${failLabel} — HTTP status:`, status, statusText ?? '')
        console.error(`${failLabel} — response body (exact):`, data)
        try {
          console.error(
            `${failLabel} — response body (JSON string):`,
            typeof data === 'string' ? data : JSON.stringify(data, null, 2),
          )
        } catch {
          console.error(`${failLabel} — response body could not be stringified`)
        }
        console.error(`${failLabel} — response headers:`, headers)
      } else {
        console.error(`${failLabel} — no response (network/client error):`, error)
        if (ax.message) console.error(`${failLabel} — message:`, ax.message)
      }

      let msg = ''
      const d = ax.response?.data
      if (typeof d === 'string') msg = d
      else if (d && typeof d === 'object') {
        const o = d as Record<string, unknown>
        msg =
          String(o.message ?? o.Message ?? o.title ?? o.error ?? o.detail ?? '')
      }
      toast.error(
        msg.trim() || (isApiUpdate ? 'Failed to update quotation.' : 'Failed to save quotation.'),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  const readOnly = status === 'cancelled'

  if (!isNew && editLoadingFromApi) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-muted-foreground">
        <p className="text-sm">Loading quotation…</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Quotations</h1>
            <p className="text-muted-foreground text-sm">{code ? `Code: ${code}` : 'Quotation Details'}</p>
          </div>
        </div>
      </div>

      {readOnly ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          This quotation is cancelled. You can view it but not save changes.
        </p>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Order Date</Label>
              <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>
                Reference <span className="text-destructive">*</span>
              </Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} disabled={readOnly} />
            </div>
            <div className="space-y-2">
              <Label>Customer</Label>
              <Select
                value={customerId || undefined}
                onValueChange={setCustomerId}
                disabled={readOnly || !lookupsReady || lookupsLoading || !branchId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !branchId ? 'Select branch first' : lookupsLoading ? 'Loading…' : 'Select customer'
                    }
                  />
                </SelectTrigger>
                <SelectContent searchable>
                  {customerRows.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethodId || undefined}
                onValueChange={setPaymentMethodId}
                disabled={readOnly || !paymentLookupsReady || paymentLookupsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={paymentLookupsLoading ? 'Loading…' : 'Select payment method'} />
                </SelectTrigger>
                <SelectContent searchable>
                  {paymentMethods.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Term</Label>
              <Select
                value={paymentTermId || undefined}
                onValueChange={setPaymentTermId}
                disabled={readOnly || !paymentLookupsReady || paymentLookupsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={paymentLookupsLoading ? 'Loading…' : 'Select payment term'} />
                </SelectTrigger>
                <SelectContent searchable>
                  {paymentTerms.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Est Delivery Date</Label>
              <Input
                type="date"
                value={estDeliveryDate}
                onChange={(e) => setEstDeliveryDate(e.target.value)}
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quotation-attachment">File</Label>
              {(() => {
                const existingUrl = resolveAttachmentUrl(serverAttachmentPath)
                const existingName = serverAttachmentPath
                  ? serverAttachmentPath.split(/[\\/]/).pop() || 'attachment'
                  : ''
                return (
                  <AttachmentField
                    inputId="quotation-attachment"
                    mode={isNew ? 'new' : 'edit'}
                    file={attachmentFile}
                    existingUrl={isNew ? '' : existingUrl}
                    existingName={isNew ? '' : existingName}
                    onFileChange={(f) => {
                      setAttachmentFile(f)
                      if (f) setAttachmentName(f.name)
                    }}
                  />
                )
              })()}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">Ordered Items</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)} disabled={readOnly}>
                <Plus className="mr-1 h-4 w-4" />
                Add Row
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)} disabled={readOnly}>
                Add 3 Rows
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)} disabled={readOnly}>
                Add 5 Rows
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {[
                      'Item',
                      'Batch',
                      'Available quantity',
                      'Quantity',
                      'Unit price',
                      'Discount %',
                      'Taxable amount',
                      'Tax',
                      'Tax amount',
                      'Total',
                      '',
                    ].map((h) => (
                      <TableHead key={h || 'x'} className={tableHeaderClass}>
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, idx) => (
                    <TableRow key={l.lineKey}>
                      <TableCell className="py-2">
                        <Select
                          value={l.itemId || undefined}
                          onValueChange={(v) => {
                            const it = itemOptionsForWarehouse.find((i) => String(i.id) === v)
                            const salesTaxId = it?.sellingTax && it.sellingTax > 0 ? String(it.sellingTax) : ''
                            const tax = taxRates.find((x) => String(x.id) === salesTaxId)
                            updateLine(idx, {
                              itemId: v,
                              unitPrice: Number(it?.unitSellingPrice || 0),
                              taxRateId: tax ? salesTaxId : defaultTaxRateId || l.taxRateId,
                            })
                            void loadBatchesForItem(v)
                          }}
                          disabled={readOnly || !warehouseId || itemsForWarehouseLoading}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue
                              placeholder={
                                !warehouseId
                                  ? 'Select warehouse first'
                                  : itemsForWarehouseLoading
                                    ? 'Loading items…'
                                    : itemOptionsForWarehouse.length === 0
                                      ? 'No items for this warehouse'
                                      : 'Select item'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent searchable>
                            {itemOptionsForWarehouse.map((it) => (
                              <SelectItem key={it.id} value={String(it.id)}>
                                {it.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2">
                        <Select
                          value={l.batchId || undefined}
                          onValueChange={(v) => {
                            updateLine(idx, { batchId: v })
                            void refreshAvailableQuantity(idx, { itemId: l.itemId, batchId: v })
                          }}
                          disabled={!l.itemId || readOnly}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue
                              placeholder={
                                !l.itemId
                                  ? 'Select item first'
                                  : batchesForLineItem(l.itemId).length === 0
                                    ? 'No batches available'
                                    : 'Select batch'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent searchable>
                            {batchesForLineItem(l.itemId).map((b) => (
                              <SelectItem key={b.id} value={String(b.id)}>
                                {b.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          readOnly
                          className="h-9 bg-muted/50 text-center"
                          value={l.availableQuantity || rowAvailable(l) || 0}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          className="h-9 text-center"
                          value={l.quantity ?? ''}
                          onChange={(e) => {
                            const avail = Number(l.availableQuantity || rowAvailable(l) || 0)
                            const raw = Number(e.target.value) || 0
                            const qty = avail > 0 ? Math.min(raw, avail) : raw
                            updateLine(idx, { quantity: qty })
                          }}
                          disabled={readOnly}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          readOnly
                          className="h-9 text-center bg-muted/50"
                          value={l.unitPrice ?? ''}
                          disabled={readOnly}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          readOnly
                          className="h-9 text-center bg-muted/50"
                          value={l.discountPercent ?? ''}
                          title="Discount comes from the active scheme"
                          disabled={readOnly}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 bg-muted/50 text-center" value={lineTaxableAmount(l).toFixed(2)} />
                      </TableCell>
                      <TableCell className="py-2 min-w-[180px]">
                        <Select
                          value={l.taxRateId || undefined}
                          onValueChange={(v) => updateLine(idx, { taxRateId: v })}
                          disabled={readOnly || taxRatesLoading}
                        >
                          <SelectTrigger className="h-9 min-w-[180px]">
                            <SelectValue
                              placeholder={taxRatesLoading ? 'Loading tax…' : 'Select tax rate'}
                            />
                          </SelectTrigger>
                          <SelectContent searchable>
                            {taxSelectOptions.map((tr) => (
                              <SelectItem key={tr.value} value={tr.value}>
                                {tr.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 bg-muted/50 text-center" value={lineTaxAmount(l).toFixed(2)} />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 bg-muted/50 text-center" value={lineTotal(l).toFixed(2)} />
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          disabled={readOnly}
                          onClick={() => removeLine(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-6 rounded-md border border-border/60 bg-primary/5 p-4 shadow-sm dark:bg-primary/10">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sub total</Label>
                  <p className="text-lg font-semibold tabular-nums">{totals.subTotal.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Discount</Label>
                  <p className="text-lg font-semibold tabular-nums text-muted-foreground">{totals.discount.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total tax</Label>
                  <p className="text-lg font-semibold tabular-nums">{totals.totalTax.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Net amount</Label>
                  <p className="text-lg font-bold tabular-nums">{totals.net.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate('/inventory/quotations')}>
            Back
          </Button>
          <Button type="submit" disabled={readOnly || isSaving}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? (isNew ? 'Saving…' : 'Updating…') : isNew ? 'Save' : 'Update'}
          </Button>
        </div>
      </form>
    </div>
  )
}
